/* eslint-disable no-param-reassign */
import React from 'react';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { InfoBlock } from '@picsio/ui';

import l10n from '../../shared/strings';
import {
  getLiveChannelNames,
  isLastLiveVariantsOnChannel,
  markProductChannelsAsDraft,
} from './helpers/channelHelpers';
import { syncProductHasVariants } from './helpers/productVariantsHelpers';
import { pruneSelection } from './helpers/applySelection';
import { buildSelectionKey } from './helpers/selectionKey';

export const removeMasterVariant = createAsyncThunk(
  'products/removeMasterVariant',
  async ({ productId, variantId, listingIds }, {
    extra: {
      sdk, Logger, Toast,
    },
    getState,
  }) => {
    try {
      const { items, entities } = getState().products;
      const productsState = getState().products;
      const product = items.find(({ _id }) => _id === productId);

      await sdk.products.deleteVariant(variantId, listingIds.length ? listingIds : undefined);

      const variant = product?.variants?.find(({ _id }) => _id === variantId);

      const liveChannelNames = getLiveChannelNames(productsState, variant);

      const channelsToUnpublish = listingIds.length
        ? liveChannelNames.filter((channelName) => isLastLiveVariantsOnChannel(product, [variantId], channelName, productsState))
        : [];

      const listingVariantIds = Object.values(entities?.listingVariants || {})
        .filter((listingVariant) => listingVariant.masterVariantId === variantId)
        .map((listingVariant) => listingVariant._id)
        .filter(Boolean);

      return {
        productId, variantId, listingVariantIds, channelsToUnpublish,
      };
    } catch (error) {
      Toast('Variant is not deleted. Refresh and try again', { autoClose: false, type: 'error' });
      Logger.error(new Error('Error delete product variant'), { error }, [
        'DeleteProductVariantFailed',
        { errorMessage: 'Variant is not deleted on the backend.' },
      ]);
      throw error;
    }
  },
  {
    condition: async ({ productId, variantId }, { getState, extra: { showDialogAsync } }) => {
      const productsState = getState().products;
      const product = productsState.items.find(({ _id }) => _id === productId);
      const variant = product?.variants?.find(({ _id }) => _id === variantId);

      const liveChannelNames = getLiveChannelNames(productsState, variant);

      const isLastLiveVariantOnAnyChannel = liveChannelNames.some(
        (channelName) => isLastLiveVariantsOnChannel(product, [variantId], channelName, productsState),
      );

      if (!isLastLiveVariantOnAnyChannel) return true;

      try {
        await showDialogAsync({
          title: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_VARIANT.TITLE,
          text: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_VARIANT.TEXT_WITH_CHANNELS(variant?.title || variantId, liveChannelNames),
          children: <InfoBlock className="m-t-16">{l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_VARIANT.LAST_LIVE_VARIANT_NOTE}</InfoBlock>,
          textBtnCancel: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_VARIANT.BTN_CANCEL,
          textBtnOk: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_VARIANT.BTN_OK,
          buttonOkColor: 'danger',
        }).promise;
        return true;
      } catch {
        return false;
      }
    },
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      removeMasterVariant.pending,
      (state, { meta }) => {
        const { productId } = meta.arg;
        state.items.forEach((product) => {
          if (product._id === productId) {
            product.isBusy = true;
          }
        });
      },
    )
    .addCase(
      removeMasterVariant.fulfilled,
      (state, { payload }) => {
        const {
          productId, variantId, listingVariantIds, channelsToUnpublish,
        } = payload;

        state.items.forEach((product) => {
          if (product._id !== productId) return;

          product.isBusy = false;
          product.variants = product.variants.filter((variant) => variant._id !== variantId);
          syncProductHasVariants(product);

          markProductChannelsAsDraft(product, channelsToUnpublish, state);
        });

        const variantIdStr = String(variantId);
        const listingSet = new Set(listingVariantIds.map(String));
        const prevLength = state.search.items.length;
        state.search.items = state.search.items.filter((item) => {
          const id = String(item._id);
          const masterVariant = item.masterVariantId != null ? String(item.masterVariantId) : '';

          return id !== variantIdStr
            && masterVariant !== variantIdStr
            && !listingSet.has(id);
        });
        const removedCount = prevLength - state.search.items.length;
        if (removedCount > 0) {
          state.search.total = Math.max(0, (state.search.total || 0) - removedCount);
        }

        const remainingKeys = new Set(state.search.items.map(buildSelectionKey));
        const staleKeys = state.ui.selection.keys.filter((key) => !remainingKeys.has(key));
        state.ui.selection = pruneSelection(state.ui.selection, staleKeys);
      },
    )
    .addCase(
      removeMasterVariant.rejected,
      (state, { meta, error }) => {
        state.items.forEach((product) => {
          if (product._id === meta.arg.productId) {
            product.isBusy = false;
          }
        });
        state.error = error;
      },
    );
};
